import React, { useEffect, useState } from "react";
import Form from "@rjsf/mui";
import validator from "@rjsf/validator-ajv8";
import { achFormSchema } from "./formSchema";
import { achUiSchema } from "./uiSchema";
import TaxIdWidget from "../widgets/TaxIdWidget";
import NumericWidget from "../widgets/NumericWidget";
import CurrencyWidget from "../widgets/CurrencyWidget";
import ObjectFieldTemplate from "../templates/ObjectFieldTemplate";
import TitleFieldTemplate from "../templates/TitleFieldTemplate";
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  createTheme,
  ThemeProvider,
  Link,
  Alert,
  CircularProgress,
} from "@mui/material";
import Grid from "@mui/material/GridLegacy";
import { IChangeEvent } from "@rjsf/core";
import { RJSFSchema } from "@rjsf/utils";
import styles from "../form.module.css";
import img from "../assets/657507_tmpl_head_60db3e2024ea6.png";
import { convertJSONToMultiPartFormData } from "../utils/general";
import { host } from "config";
import FormSubmittedSplashPage from "../formSubmittedSplashPage";
import SaveAndResumeDialog from "../saveAndResume/saveAndResumeDialog";
import { useParams } from "react-router-dom";
import ConfirmResumeOTCDialog from "../saveAndResume/confirmResumeOTC";

const formNumber = "5";

const fileFields = ["w9FileUpload", "bankingDocumentUpload"];

const sensitiveKeys = [
  "taxId",
  "w9FileUpload",
  "routingNumber",
  "accountNumber",
  "bankingDocumentUpload",
  "date",
  "typedSignature",
];

const theme = createTheme({
  palette: {
    primary: {
      main: "#2196f3",
    },
    background: {
      default: "#ffffff",
    },
  },
  typography: {
    fontFamily: '"Open Sans", Arial, sans-serif',
  },
  components: {
    MuiTextField: {
      defaultProps: {
        variant: "outlined",
        size: "small",
        InputLabelProps: {
          shrink: true,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
        },
      },
    },
    MuiFormGroup: {
      styleOverrides: {
        root: {
          alignItems: "flex-start",
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "flex-start",
          marginLeft: 0,
          width: "100%",
          marginTop: 0,
          marginBottom: 0,
        },
        label: {
          marginLeft: "8px", // spacing between radio and text
          textAlign: "left",
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        MenuProps: {
          PaperProps: {
            style: {
              maxHeight: 300,
              maxWidth: 300,
            },
          },
        },
      },
      styleOverrides: {
        select: {
          width: "700px",
          boxSizing: "border-box",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          display: "block",
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          whiteSpace: "normal",
          wordBreak: "break-word",
          alignItems: "flex-start",
          paddingTop: 8,
          paddingBottom: 8,
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          width: "100%",
        },
      },
    },
  },
});

const widgets = {
  TaxIdWidget: TaxIdWidget,
  NumericWidget: NumericWidget,
  CurrencyWidget: CurrencyWidget,
};

// Custom field for instructions with bullets
const InstructionsField = () => {
  return (
    <Box sx={{ mb: 3 }}>
      <ul
        style={{
          margin: 0,
          paddingLeft: "20px",
          lineHeight: 1.4,
        }}
        className="ach-instructions-list"
      >
        <li>To sign up for EFT, please fill below. If you wish to decline EFT then select decline.</li>
        <li>
          If you are an agent or a broker submitting an ACH for Commissions, please select "Direct Deposit for
          Commissions"
        </li>
        <li>
          Any account changes must be reported to Third Party Administrator thirty (30) days prior to actual change.
        </li>
      </ul>
    </Box>
  );
};

// Custom field for section title
const SectionTitleField = ({ schema }) => {
  return (
    <Box sx={{ mb: 1.5, mt: 2 }}>
      <Typography variant="body1" sx={{ fontWeight: 500, fontSize: "0.95rem", color: "#424242", lineHeight: 1.4 }}>
        {schema.title}
      </Typography>
    </Box>
  );
};

// Required field note box
const RequiredFieldNoteField = () => {
  const ErrorIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" style={{ display: "block" }}>
      <circle cx="10" cy="10" r="9" fill="#d32f2f" />
      <text x="10" y="15" fontSize="14" fontWeight="bold" fill="white" textAnchor="middle">
        !
      </text>
    </svg>
  );

  return (
    <Alert
      severity="error"
      icon={<ErrorIcon />}
      sx={{
        mb: 2,
        backgroundColor: "#ffebee",
        border: "1px solid #f44336",
        "& .MuiAlert-message": {
          color: "#424242",
          fontSize: "0.9rem",
          fontWeight: 400,
        },
      }}
    >
      Required field
    </Alert>
  );
};
// Custom field for question text
const QuestionTextField = ({ schema }) => {
  return (
    <Typography variant="body1" sx={{ mb: 2, fontSize: "0.95rem", fontWeight: 500, color: "#424242", lineHeight: 1.4 }}>
      {schema.title}
    </Typography>
  );
};
// Custom field for option note
const OptionNoteField = ({ schema }) => {
  return (
    <Box
      sx={{
        mb: 3,
        mt: 2,
        width: "100%",
        display: "block",
        clear: "both",
        gridColumn: "1 / -1",
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontSize: "0.85rem",
          color: "rgb(12, 38, 89)",
          lineHeight: 1.4,
          fontWeight: 700,
          display: "block",
        }}
      >
        {schema.title}
      </Typography>
    </Box>
  );
};

// Custom field for signature note
const SignatureNoteField = ({ schema, registry }) => {
  return (
    <Box sx={{ mt: -1, mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <Typography variant="body2" sx={{ fontSize: "0.85rem", color: "#666" }}>
        {schema.title}
      </Typography>
      <Link
        href="#"
        onClick={(e) => {
          e.preventDefault();
          if (registry.formContext && registry.formContext.clearSignature) {
            registry.formContext.clearSignature();
          }
        }}
        sx={{ fontSize: "0.85rem", textDecoration: "underline" }}
      >
        clear
      </Link>
    </Box>
  );
};

// Custom field for authorization text with bullet points
const AuthorizationTextField = (props) => {
  const { registry } = props;
  const formData = registry?.formContext?.formData || {};
  const isGroups = formData.achType === "ACH Premium Payment Authorization for Groups";
  const isFSA = formData.achType === "ACH Flex (FSA) Premium Payment Authorization";
  const isBroker = formData.achType === "Broker Commission Payment Setup";

  // Different bullet points for Groups/FSA vs Broker
  const bulletPoints =
    isGroups || isFSA
      ? [
          "For the purpose of paying monthly insurance premiums that can vary due to rate and census changes.",
          "We hereby authorize UGP to initiate debit entries to our account in the financial institution identified above and also credit entries, if necessary, for any debit entries that are determined to be in error. We additionally authorize the financial institution to debit or credit to our account.",
          "This authority is to remain in effect until revoked by us in writing to your Third Party Administrator.",
        ]
      : [
          "We hereby authorize our Third Party Administrator to initiate debit entries to our account in the financial institution identified above and also credit entries, if necessary, for any debit entries that are determined to be in error. We additionally authorize the financial institution to debit or credit to our account.",
          "This authority is to remain in effect until revoked by us in writing to your Third Party Administrator.",
        ];

  return (
    <Box sx={{ my: 3 }}>
      <ul style={{ margin: 0, paddingLeft: "20px", lineHeight: 1.4, fontSize: "0.9rem", color: "rgb(12, 38, 89)" }}>
        {bulletPoints.map((point, index) => (
          <li key={index}>
            <Typography
              variant="body2"
              sx={{ fontSize: "0.9rem", color: "rgb(12, 38, 89)", lineHeight: 1.4, fontWeight: 700 }}
            >
              {point}
            </Typography>
          </li>
        ))}
      </ul>
    </Box>
  );
};

// Custom Object Field Template for ACH form
const AchObjectFieldTemplate = (props) => {
  const { properties } = props;

  return (
    <Box>
      {properties.map((element, index) => {
        const fieldName = element.content.props.name;

        // Check if this is monthlyOrOneTimePull - render with submittedOneTimePayment side by side
        if (fieldName === "monthlyOrOneTimePull") {
          // Find submittedOneTimePayment field
          const submittedPaymentIndex = properties.findIndex((p) => p.content.props.name === "submittedOneTimePayment");

          if (submittedPaymentIndex !== -1) {
            const submittedPaymentElement = properties[submittedPaymentIndex];

            return (
              <Box key={index}>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    {element.content}
                  </Grid>
                  <Grid item xs={6}>
                    {submittedPaymentElement.content}
                  </Grid>
                </Grid>
              </Box>
            );
          }
        }

        // Skip rendering submittedOneTimePayment separately since it's rendered with monthlyOrOneTimePull
        if (fieldName === "submittedOneTimePayment") {
          return null;
        }

        // Check if this is Name of Broker on Record and Name of Company - render them side by side (for Broker option)
        if (fieldName === "nameOfBrokerOnRecord") {
          // Find Name of Company field
          const companyIndex = properties.findIndex((p) => p.content.props.name === "nameOfCompany");

          if (companyIndex !== -1) {
            const companyElement = properties[companyIndex];

            return (
              <Box key={index}>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    {element.content}
                  </Grid>
                  <Grid item xs={6}>
                    {companyElement.content}
                  </Grid>
                </Grid>
              </Box>
            );
          }
        }

        // Check if this is Name of Company or Group Number - render them side by side
        if (fieldName === "nameOfCompany") {
          // Find Group Number field
          const groupNumberIndex = properties.findIndex((p) => p.content.props.name === "groupNumber");

          if (groupNumberIndex !== -1) {
            const groupNumberElement = properties[groupNumberIndex];

            return (
              <Box key={index}>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    {element.content}
                  </Grid>
                  <Grid item xs={6}>
                    {groupNumberElement.content}
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mt: 0.5,
                        fontSize: "0.75rem",
                        color: "#666",
                        lineHeight: 1.4,
                      }}
                    >
                      This is the number associated with the group you completing this transaction for. If you are
                      unsure of what to put here, please reach out to your account manager.
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            );
          }
        }

        // Skip rendering Group Number separately since it's rendered with Name of Company
        if (fieldName === "groupNumber") {
          return null;
        }

        // Render location with description below
        if (fieldName === "location") {
          return (
            <Box key={index} sx={{ mb: 2 }}>
              {element.content}
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 0.5,
                  fontSize: "0.75rem",
                  color: "#666",
                  lineHeight: 1.4,
                }}
              >
                Please enter the location name or number for all locations that apply if paying for individual locations
                seperately. Leave blank if paying for the group as a whole.
              </Typography>
            </Box>
          );
        }

        // Render nameOfCobraMember (full width)
        if (fieldName === "nameOfCobraMember") {
          return (
            <Box key={index} sx={{ mb: 2 }}>
              {element.content}
            </Box>
          );
        }

        // Render brokerGroupNumber with description below
        if (fieldName === "brokerGroupNumber") {
          return (
            <Box key={index} sx={{ mb: 2 }}>
              {element.content}
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 0.5,
                  fontSize: "0.75rem",
                  color: "#666",
                  lineHeight: 1.4,
                }}
              >
                This is the number associated with the group you completing this transaction for. If you are unsure of
                what to put here, please reach out to your account manager.
              </Typography>
            </Box>
          );
        }

        // Render taxId and email side by side
        if (fieldName === "taxId") {
          // Find email field
          const emailIndex = properties.findIndex((p) => p.content.props.name === "email");

          if (emailIndex !== -1) {
            const emailElement = properties[emailIndex];

            return (
              <Box key={index}>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    {element.content}
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mt: 0.5,
                        fontSize: "0.75rem",
                        color: "rgb(12, 38, 89)",
                        lineHeight: 1.4,
                        fontWeight: 700,
                      }}
                    >
                      Number only, no dash
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    {emailElement.content}
                  </Grid>
                </Grid>
              </Box>
            );
          }
        }

        // Skip rendering email separately only if taxId exists in properties (was rendered with taxId)
        if (fieldName === "email") {
          const taxIdExists = properties.some((p) => p.content.props.name === "taxId");
          if (taxIdExists) {
            return null; // Skip because it was rendered with taxId
          }
          // Otherwise render email by itself
          return (
            <Box key={index} sx={{ mb: 2 }}>
              {element.content}
            </Box>
          );
        }

        // Render file upload fields with proper width constraints
        if (fieldName === "w9FileUpload" || fieldName === "bankingDocumentUpload") {
          return (
            <Box key={index} sx={{ mb: 2, width: "100%", maxWidth: "100%", overflow: "hidden" }}>
              {element.content}
            </Box>
          );
        }

        return (
          <Box
            key={index}
            sx={{
              width: "100%",
              mb: 2,
            }}
          >
            {element.content}
          </Box>
        );
      })}
    </Box>
  );
};

// Custom fields
const fields = {
  instructionsField: InstructionsField,
  questionTextField: QuestionTextField,
  sectionTitleField: SectionTitleField,
  requiredFieldNote: RequiredFieldNoteField,
  optionNoteField: OptionNoteField,
  signatureNoteField: SignatureNoteField,
  authorizationTextField: AuthorizationTextField,
  // cityStateZipField: CityStateZipField,
  // fiCityStateZipField: FiCityStateZipField,
};

// Custom templates
const templates = {
  ObjectFieldTemplate: AchObjectFieldTemplate,
  TitleFieldTemplate: TitleFieldTemplate,
};

interface FormData {
  achType: any;
  monthlyOrOneTimePull: any;
  submittedOneTimePayment: any;
  employerBrokerMember: any;
}

function AchAgreementForm() {
  const [formData, setFormData] = useState({
    achType: undefined,
    monthlyOrOneTimePull: undefined,
    submittedOneTimePayment: undefined,
    employerBrokerMember: undefined,
  });
  const [strippedFormData, setStrippedFormData] = useState({});
  const [showIntro, setShowIntro] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [showSubmittedPage, setShowSubmittedPage] = useState(false);
  const [openSaveAndResume, setOpenSaveAndResume] = useState(false);
  const [openConfirmResumeOTC, setOpenConfirmResumeOTC] = useState(false);

  const { guid } = useParams();

  useEffect(() => {
    if (guid && guid != "") {
      setOpenConfirmResumeOTC(true);
      setShowIntro(false);
    }
  }, [guid]);

  const updateFormData = (updates) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  const clearSignature = () => {
    setFormData((prev) => ({ ...prev, applicantSignature: "" }));
  };

  const getHiddenFields = (dynamicUiSchema) => {
    return Object.keys(dynamicUiSchema).filter((key) => dynamicUiSchema[key]["ui:widget"] === "hidden");
  };

  const stripSensitiveFields = (formData: { [key: string]: any }) => {
    const strippedFormData = { ...formData };
    sensitiveKeys.map((key) => {
      delete strippedFormData[key];
    });
    setStrippedFormData(strippedFormData);
  };

  const handleOenSaveAndResume = () => {
    console.log(formData);
    stripSensitiveFields(formData);
    setOpenSaveAndResume(true);
  };

  const handleCloseSaveAndResume = () => {
    setOpenSaveAndResume(false);
  };

  const handleCloseConfirmResumeOTC = () => {
    setOpenConfirmResumeOTC(false);
  };

  const handleSubmit = (data: IChangeEvent<FormData>) => {
    setIsLoading(true);

    const hiddenFields = getHiddenFields(dynamicUiSchema);
    const cleanedData = { ...formData };

    hiddenFields.forEach((field) => delete cleanedData[field]);

    fetch(`${host}/api/implementation/forms/ach`, {
      method: "POST",
      body: convertJSONToMultiPartFormData(cleanedData, fileFields),
    })
      .then((response) => {
        if (!response.ok) {
          response.text().then((text) => {
            throw new Error(text);
          });
        }
        response.json().then(async (data: any) => {
          setIsLoading(false);

          setShowSubmittedPage(true);
        });
      })
      .catch((error: any) => {
        console.error("Error:", error);
        alert(
          "There was an error while submitting the form. Please try again later and contact IT if the problem persists."
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleChange = (data: IChangeEvent<FormData>) => {
    setFormData(data.formData);
  };

  const handleError = (errors) => {
    console.log("Form errors:", errors);
  };

  // Build dynamic UI schema based on selections
  const dynamicUiSchema = React.useMemo(() => {
    const baseUiSchema = { ...achUiSchema };

    const isGroups = formData.achType === "ACH Premium Payment Authorization for Groups";
    const isCOBRA = formData.achType === "ACH Premium Payment for COBRA Member";
    const isFSA = formData.achType === "ACH Flex (FSA) Premium Payment Authorization";
    const isBroker = formData.achType === "Broker Commission Payment Setup";

    // Show Groups fields for Groups, COBRA, and FSA
    if (!isGroups && !isCOBRA && !isFSA) {
      baseUiSchema.monthlyOrOneTimePull = { "ui:widget": "hidden" };
      baseUiSchema.employerBrokerMember = { "ui:widget": "hidden" };
      baseUiSchema.nameOfCompany = { "ui:widget": "hidden" };
      baseUiSchema.groupNumber = { "ui:widget": "hidden" };
      baseUiSchema.submittedOneTimePayment = { "ui:widget": "hidden" };
      baseUiSchema.usePreviousBankInfo = { "ui:widget": "hidden" };
      baseUiSchema.location = { "ui:widget": "hidden" };
      baseUiSchema.nameOfBrokerOnRecord = { "ui:widget": "hidden" };
      baseUiSchema.nameOfCobraMember = { "ui:widget": "hidden" };
    } else {
      // Only show submittedOneTimePayment if One-Time is selected
      if (formData.monthlyOrOneTimePull !== "One-Time") {
        baseUiSchema.submittedOneTimePayment = { "ui:widget": "hidden" };
        baseUiSchema.usePreviousBankInfo = { "ui:widget": "hidden" };
      } else {
        // Only show usePreviousBankInfo if submittedOneTimePayment is 'Yes'
        if (formData.submittedOneTimePayment !== "Yes") {
          baseUiSchema.usePreviousBankInfo = { "ui:widget": "hidden" };
        }
        // Show one time payment amount field if One-Time is selected
        baseUiSchema.oneTimeAmount = { "ui:widget": "CurrencyWidget" };
      }

      // Show/hide fields based on Employer/Broker/Member selection
      const employerBrokerMember = formData.employerBrokerMember;

      if (employerBrokerMember === "Employer") {
        // Show: Name of Company, Group Number, Location
        baseUiSchema.nameOfBrokerOnRecord = { "ui:widget": "hidden" };
        baseUiSchema.nameOfCobraMember = { "ui:widget": "hidden" };
      } else if (employerBrokerMember === "Broker") {
        // Show: Name of Broker on Record, Name of Company, Group Number
        baseUiSchema.location = { "ui:widget": "hidden" };
        baseUiSchema.nameOfCobraMember = { "ui:widget": "hidden" };
      } else if (employerBrokerMember === "Member") {
        // Show: Name of Company, Group Number, Name of COBRA Member
        baseUiSchema.location = { "ui:widget": "hidden" };
        baseUiSchema.nameOfBrokerOnRecord = { "ui:widget": "hidden" };
      } else {
        // Hide all if nothing selected
        baseUiSchema.location = { "ui:widget": "hidden" };
        baseUiSchema.nameOfBrokerOnRecord = { "ui:widget": "hidden" };
        baseUiSchema.nameOfCobraMember = { "ui:widget": "hidden" };
      }
    }

    // Show Broker fields only for Broker Commission Payment Setup
    if (!isBroker) {
      baseUiSchema.brokerName = { "ui:widget": "hidden" };
      baseUiSchema.commissionsPayableTo = { "ui:widget": "hidden" };
      baseUiSchema.brokerGroupNumber = { "ui:widget": "hidden" };
      baseUiSchema.w9Instruction = { "ui:widget": "hidden" };
      baseUiSchema.w9FileUpload = { "ui:widget": "hidden" };
    }

    // Show Tax ID for Groups and FSA only, hide for COBRA and Broker
    if (isCOBRA || isBroker) {
      baseUiSchema.taxId = { "ui:widget": "hidden" };
    }

    // Show authorization text for Groups, FSA, and Broker, hide for COBRA
    if (isCOBRA) {
      baseUiSchema.authorizationText = { "ui:widget": "hidden" };
    }

    return baseUiSchema;
  }, [
    formData.achType,
    formData.monthlyOrOneTimePull,
    formData.submittedOneTimePayment,
    formData.employerBrokerMember,
  ]);

  if (isLoading) {
    return (
      <div className="loader-container">
        <CircularProgress />
      </div>
    );
  }

  if (showSubmittedPage) {
    return <FormSubmittedSplashPage formName="ACH" />;
  }

  if (showIntro) {
    // Introduction Page (Page 2 of 3 from screenshot)
    return (
      <ThemeProvider theme={theme}>
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              backgroundColor: "#ffffff",
              borderRadius: 2,
              border: "2px solid #000",
              boxShadow: "none",
            }}
          >
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <img
                src={img}
                alt="OptiMed Health"
                style={{
                  maxWidth: "350px",
                  height: "auto",
                  display: "block",
                  margin: "0 auto",
                }}
              />
            </Box>
            <Box
              sx={{
                backgroundColor: "#1a365d",
                color: "white",
                py: 3,
                px: 4,
                mb: 4,
                textAlign: "center",
                fontSize: "1.75rem",
                fontWeight: 400,
                borderRadius: 2,
              }}
            >
              ACH Premium payment Authorization Agreement
            </Box>

            <Typography
              variant="body1"
              sx={{
                textAlign: "center",
                fontSize: "1.1rem",
                color: "#555",
                lineHeight: 1.4,
                mb: 4,
                px: 2,
              }}
            >
              We encourage all our groups to sign up for ACH as it is the fastest and most cost effective way to pay for
              your invoices. If you would like to pay via ACH, please select Next.
            </Typography>

            {/* Page indicator with progress bar - centered */}
            <Box sx={{ textAlign: "center", mt: 4, mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Page 2 of 3
              </Typography>
              {/* Progress bar */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mb: 3,
                }}
              >
                <Box
                  sx={{
                    width: "150px",
                    height: "4px",
                    backgroundColor: "#d3d3d3",
                    borderRadius: "2px",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      width: "66.66%",
                      height: "100%",
                      backgroundColor: "#808080",
                      transition: "width 0.3s ease",
                    }}
                  />
                </Box>
              </Box>
            </Box>

            {/* Next button aligned right */}
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                onClick={() => setShowIntro(false)}
                sx={{
                  backgroundColor: "#5a6c7d",
                  color: "white",
                  px: 4,
                  py: 1.5,
                  textTransform: "none",
                  fontWeight: 600,
                  minWidth: "150px",
                  "&:hover": {
                    backgroundColor: "#4a5c6d",
                  },
                }}
              >
                Next
              </Button>
            </Box>
          </Paper>
        </Container>
      </ThemeProvider>
    );
  }

  // Form Page (Page 3 of 3 from screenshot)
  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            backgroundColor: "#ffffff",
            borderRadius: 2,
            border: "2px solid #000",
            boxShadow: "none",
            overflow: "hidden",
          }}
        >
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <img
              src={img}
              alt="OptiMed Health"
              style={{
                maxWidth: "350px",
                height: "auto",
                display: "block",
                margin: "0 auto",
              }}
            />
          </Box>
          <Box
            sx={{
              backgroundColor: "#1a365d",
              color: "white",
              py: 2.5,
              px: 3,
              mb: 3,
              textAlign: "center",
              fontSize: "28px",
              fontWeight: 500,
              borderRadius: 2,
            }}
          >
            ACH Premium Payment Authorization Agreement / Direct Deposit Form for Commissions
          </Box>

          <Box sx={{ width: "100%", overflow: "hidden" }}>
            <Form
              schema={achFormSchema}
              uiSchema={dynamicUiSchema}
              formData={formData}
              validator={validator}
              widgets={widgets}
              fields={fields}
              templates={templates}
              onChange={handleChange}
              onSubmit={handleSubmit}
              onError={handleError}
              showErrorList={false}
              liveValidate={false}
              formContext={{
                formData,
                updateFormData,
                clearSignature,
              }}
              className={styles["custom-form"]}
            >
              <Button
                variant="contained"
                onClick={() => handleOenSaveAndResume()}
                sx={{
                  backgroundColor: "#5a6c7d",
                  color: "white",
                  px: 3,
                  py: 1.2,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.95rem",
                  minWidth: "150px",
                  border: "none",
                  "&:hover": {
                    backgroundColor: "#4a5c6d",
                    border: "none",
                  },
                }}
              >
                Save and Resume Later
              </Button>
              {/* All in one row: Previous button, Page indicator with progress bar, Submit button */}
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", mt: 4, px: 1 }}>
                <Box sx={{ flex: "0 0 auto", display: "flex", alignItems: "flex-end" }}>
                  <Button
                    variant="contained"
                    onClick={() => setShowIntro(true)}
                    sx={{
                      backgroundColor: "#5a6c7d",
                      color: "white",
                      px: 3,
                      py: 1.2,
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "0.95rem",
                      minWidth: "150px",
                      border: "none",
                      "&:hover": {
                        backgroundColor: "#4a5c6d",
                        border: "none",
                      },
                    }}
                  >
                    Previous
                  </Button>
                </Box>

                {/* Page indicator with progress bar - center */}
                <Box
                  sx={{
                    flex: "1 1 auto",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    px: 2,
                    pb: 0,
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 0.5, whiteSpace: "nowrap", fontSize: "0.875rem" }}
                  >
                    Page 3 of 3
                  </Typography>
                  {/* Progress bar */}
                  <Box
                    sx={{
                      width: "200px",
                      height: "4px",
                      backgroundColor: "#d3d3d3",
                      borderRadius: "2px",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        backgroundColor: "#808080",
                        transition: "width 0.3s ease",
                      }}
                    />
                  </Box>
                </Box>

                <Box sx={{ flex: "0 0 auto", display: "flex", alignItems: "flex-end" }}>
                  <Button
                    type="submit"
                    variant="contained"
                    sx={{
                      backgroundColor: "#5a6c7d",
                      color: "white",
                      px: 3,
                      py: 1.2,
                      // textTransform: "uppercase",
                      fontWeight: 600,
                      fontSize: "0.95rem",
                      minWidth: "150px",
                      "&:hover": {
                        backgroundColor: "#4a5c6d",
                      },
                    }}
                  >
                    Submit
                  </Button>
                </Box>
              </Box>
            </Form>
          </Box>
        </Paper>
      </Container>
      <SaveAndResumeDialog
        open={openSaveAndResume}
        onClose={handleCloseSaveAndResume}
        formNumber={formNumber}
        strippedFormData={strippedFormData}
      ></SaveAndResumeDialog>
      <ConfirmResumeOTCDialog
        open={openConfirmResumeOTC}
        onClose={handleCloseConfirmResumeOTC}
        guid={guid}
        setFormData={setFormData}
      />
    </ThemeProvider>
  );
}

export default AchAgreementForm;
